import { useState } from 'react'

import * as Firestore from 'src/helpers/firestore'

import { buildComponent } from 'src/components/factory'
import { AudioBackground, Category } from 'src/state/models/audio'

import { useAudioFile } from 'src/hooks/use-audio-file'
import { useAudioConstraints } from 'src/hooks/use-audio-constraints'
import { useAudioConfiguration } from 'src/hooks/use-audio-configuration'
import { useStore, useStoreState } from 'src/hooks/state'
import { useOnMount } from 'src/hooks/use-on-mount'

import { PlayerState } from './state'

import * as UI from 'native-base'
import * as Semantics from 'src/components/semantics'
import * as Components from './components'

export const ScreenConnectedPlayer = buildComponent()
  .withLifecycle(() => {
    const Store = useStore()
    const configuration = useAudioConstraints()
    const audioConfiguration = useAudioConfiguration()
    const files = useAudioFile()
    const categories = useStoreState(
      (store) => store.space.info?.subscription?.audio?.categories ?? [],
    )

    const [backgroundPanel, showBackgroundPanel] = useState(false)
    const [breathHelper, setBreathHelper] = useState(false)
    const toggleBreathHelper = () => setBreathHelper((b) => !b)
    const isFavorite = useStoreState(
      (store) =>
        !!configuration.background &&
        store.audio.isFavorite({
          theme: configuration.theme,
          background: configuration.background,
        }),
    )

    // Fonction pour obtenir le titre de la catégorie
    const getCategoryTitle = () => {
      if (audioConfiguration.category) {
        const selectedCategory = categories.find(
          (cat: Category) => cat.slug === audioConfiguration.category,
        )
        if (selectedCategory && selectedCategory.type !== 'background') {
          return (
            selectedCategory.long[audioConfiguration.locale] ||
            selectedCategory.long.fr ||
            selectedCategory.short[audioConfiguration.locale] ||
            selectedCategory.short.fr
          )
        } else if (selectedCategory) {
          return (
            selectedCategory?.short[audioConfiguration.locale] ||
            selectedCategory?.short.fr
          )
        }
      }
      return null
    }

    const themeTitle = configuration.theme?.title
    const bookTitle = configuration.book?.title.long
    const categoryTitle = getCategoryTitle()

    const mainTitle = themeTitle ??
      bookTitle ??
      categoryTitle ?? {
        fr: 'Ambiance sonore',
        en: 'Music only',
      }
    const mainCover =
      (configuration.theme &&
        `images/themes/${configuration.theme.slug}/${
          configuration.theme.cover ?? 'cover'
        }.png`) ??
      (configuration.book &&
        `images/books/${configuration.book.slug}/${
          configuration.book.cover ?? 'cover'
        }.png`) ??
      'images/themes/music-only/cover.png'

    const backgroundTitle = configuration.background?.long ?? ''
    const backgroundImage =
      (configuration.background &&
        configuration.background.slug &&
        configuration.background.slug !== 'no-background' &&
        `images/backgrounds/${configuration.background.slug}/${
          configuration.background.image ?? configuration.background.slug
        }.webp`) ??
      ''

    const toggleFavorite =
      configuration.theme || !configuration.book
        ? () =>
            Store.getActions().audio.toggleFavorite({
              theme: configuration.theme,
              background: configuration.background as AudioBackground,
            })
        : undefined

    useOnMount(() => {
      Firestore.sendEvent('audio-play', {
        napId: configuration.nap?._id,
        theme: configuration.theme?._id,
        book: configuration.book?._id,
        bookChapter: configuration.bookChapterIndex,
        background: configuration.background?._id,
        duration: configuration.duration,
        locale: configuration.locale,
      })
    })

    return {
      main: files.mainCache,
      background: files.backgroundCache,
      mainTitle,
      mainCover,
      backgroundTitle,
      backgroundImage,
      breathHelper,
      toggleBreathHelper,
      isFavorite,
      toggleFavorite,
      backgroundPanel,
      showBackgroundPanel,
      downloadError: files.downloadError,
    }
  })
  .withRender(({ lifecycle }) => {
    return (
      <PlayerState.Provider>
        <UI.Column
          mx={[0, 'auto']}
          width="100%"
          maxWidth={['100%', 600]}
          flex={1}
        >
          <Components.SectionButtons />

          {!lifecycle.breathHelper && (
            <>
              <Components.SectionAlbumCover mainCover={lifecycle.mainCover} />

              <UI.Box flex={-1}>
                <Components.SectionTitle>
                  <Semantics.Localized>
                    {lifecycle.mainTitle}
                  </Semantics.Localized>
                </Components.SectionTitle>

                <Components.SectionProgress />

                <Components.SectionControls
                  backgroundImage={lifecycle.backgroundImage || ''}
                  isFavorite={lifecycle.isFavorite}
                  toggleFavorite={lifecycle.toggleFavorite}
                  showBackgroundPanel={() =>
                    lifecycle.showBackgroundPanel(true)
                  }
                />
              </UI.Box>
            </>
          )}

          {lifecycle.breathHelper && <Components.SectionBreathHelper />}

          <Components.SectionBreathHelperToggle
            active={lifecycle.breathHelper}
            toggle={lifecycle.toggleBreathHelper}
          />
        </UI.Column>

        <Components.SectionBackgroundPanel
          open={lifecycle.backgroundPanel}
          close={() => lifecycle.showBackgroundPanel(false)}
          backgroundTitle={lifecycle.backgroundTitle}
        />

        <Components.AudioPlayer
          main={lifecycle.main}
          background={lifecycle.background}
          cover={lifecycle.mainCover}
          title={Semantics.Localized.localized(lifecycle.mainTitle)}
          album={Semantics.Localized.localized(lifecycle.backgroundTitle)}
        />

        <Components.LeavePageAlert disabled={lifecycle.downloadError} />
        <Components.AlertError isOpen={lifecycle.downloadError} />
      </PlayerState.Provider>
    )
  })
